import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  COL_PRODUCTION_REQUEST_LINES,
  COL_PRODUCTION_REQUESTS,
  COL_PRODUCTS,
} from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { deleteProductionRequestWithConfirm } from '../../../lib/deleteProductionRequest'
import { enrichProductionRequestsClientNames } from '../../../lib/crmClientNameSync'
import { loadCrmClientOptions } from '../../../lib/crmClients'
import { importCrmOrder, isCrmOrderImportEnabled } from '../../../lib/crmOrderImport'
import { isLineOverdue } from '../../../lib/scheduleCalculations'
import { ProductionStatusBadge } from '../../../components/ProductionStatusBadge'
import { ExportCsvButton } from '../../../components/ExportCsvButton'
import type { CsvColumn } from '../../../lib/exportCsv'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import { Alert, FormField, Modal, ModalActions, modalBtn } from '../../../components/ui'
import { formInputProps } from '../../../components/ui/FormField'
import {
  alert,
  btn,
  cn,
  pageLeadClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableCompactCellClass,
  tableCompactHeadClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'
import type { ProductionRequestLineRecord, ProductionRequestRecord } from '../../../types/records'

function formatDateRu(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU')
}

function formatQty(q: unknown): string {
  const n = typeof q === 'number' ? q : Number(q)
  if (!Number.isFinite(n)) return '—'
  if (Number.isInteger(n)) return String(n)
  const rounded = Math.round(n * 1e6) / 1e6
  return String(rounded).replace('.', ',')
}

export function ProductionRequestsListPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<ProductionRequestRecord[]>([])
  const [linesByRequest, setLinesByRequest] = useState<Map<string, ProductionRequestLineRecord[]>>(
    () => new Map(),
  )
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [importBusy, setImportBusy] = useState(false)
  const [importErr, setImportErr] = useState('')
  const [importProductCode, setImportProductCode] = useState('')
  const [importQty, setImportQty] = useState('100')
  const crmImportEnabled = isCrmOrderImportEnabled()
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_REQUESTS_WRITE)

  const requestsCsvColumns: CsvColumn<ProductionRequestRecord>[] = useMemo(
    () => [
      { header: 'Дата', value: (r) => formatDateRu(r.receivedAt) },
      { header: 'Клиент', value: (r) => r.clientName },
      { header: 'Статус', value: (r) => r.status },
      { header: 'Приоритет', value: (r) => r.priority ?? 3 },
      { header: 'Срочно', value: (r) => (r.clientUrgent ? 'да' : '') },
      {
        header: 'Позиций',
        value: (r) => (linesByRequest.get(r.id) ?? []).length,
      },
    ],
    [linesByRequest],
  )

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_PRODUCTION_REQUESTS).getFullList<ProductionRequestRecord>({
        sort: '-receivedAt',
      })
      const withClients = await enrichProductionRequestsClientNames(list)
      setItems(withClients)
      const ids = withClients.map((r) => r.id)
      const nextLines = new Map<string, ProductionRequestLineRecord[]>()
      if (ids.length > 0) {
        const filter = ids.map((id) => `request="${id}"`).join('||')
        const lines = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
          filter,
          expand: 'product',
        })
        for (const ln of lines) {
          const rid = typeof ln.request === 'string' ? ln.request : ''
          if (!rid) continue
          const arr = nextLines.get(rid) ?? []
          arr.push(ln)
          nextLines.set(rid, arr)
        }
        for (const [, arr] of nextLines) {
          arr.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        }
      }
      setLinesByRequest(nextLines)
      setExpandedId(null)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
      setLinesByRequest(new Map())
      setExpandedId(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    try {
      const ok = await deleteProductionRequestWithConfirm(id)
      if (!ok) return
      await load()
    } catch (e: unknown) {
      window.alert(pocketbaseErrorMessage(e, 'Не удалось удалить заявку'))
    }
  }

  async function openCrmImport() {
    if (!crmImportEnabled) {
      window.alert(
        'Импорт заказов CRM отключён. Задайте переменную VITE_CRM_ORDER_IMPORT в окружении сборки.',
      )
      return
    }
    setImportErr('')
    setImportProductCode('')
    setImportQty('100')
    try {
      const products = await pb.collection(COL_PRODUCTS).getList(1, 1, { sort: 'code' })
      const firstCode = products.items[0]?.code?.trim()
      if (firstCode) setImportProductCode(firstCode)
    } catch {
      /* без подсказки кода */
    }
    setImportOpen(true)
  }

  async function runCrmImport() {
    if (!crmImportEnabled) return
    setImportBusy(true)
    setImportErr('')
    try {
      const clients = await loadCrmClientOptions()
      if (clients.length === 0) {
        setImportErr('Клиенты CRM не загружены. Проверьте VITE_SUPABASE_URL.')
        return
      }
      const code = importProductCode.trim()
      const qty = Number(importQty.replace(',', '.'))
      if (!code) {
        setImportErr('Укажите код продукции.')
        return
      }
      if (!Number.isFinite(qty) || qty <= 0) {
        setImportErr('Укажите количество больше нуля.')
        return
      }
      const client = clients[0]
      const result = await importCrmOrder({
        clientCollection: client.source,
        clientRecordId: client.id,
        clientName: client.name,
        receivedAt: new Date().toISOString().slice(0, 10),
        clientUrgent: false,
        lines: [{ productCode: code, quantity: qty, priority: 3 }],
      })
      if (!result.ok) {
        setImportErr(result.errors.join('; '))
        return
      }
      setImportOpen(false)
      await load()
      if (result.requestId) navigate(`/production-requests/${result.requestId}`)
    } catch (e: unknown) {
      setImportErr(pocketbaseErrorMessage(e, 'Ошибка импорта'))
    } finally {
      setImportBusy(false)
    }
  }

  return (
    <div>
      <p className={pageLeadClass}>
        Дата поступления и клиент из CRM; позиции — номенклатура из продукции СУП, количество и даты по строке.
        Нажмите на строку заявки, чтобы развернуть состав заказа. Комплектация в карточке продукции; имя клиента при загрузке
        списка синхронизируется из CRM при наличии прав на запись.
      </p>
      {err && <div className={alert('err')}>{err}</div>}
      <div className={toolbarClass}>
        {canWrite && (
          <Link to="/production-requests/new" className={btn('primary')}>
            Новая заявка
          </Link>
        )}
        {canWrite && (
          <button type="button" className={btn()} onClick={() => void openCrmImport()} disabled={loading}>
            Импорт из CRM (dev)
          </button>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        {items.length > 0 && (
          <ExportCsvButton filename="production-requests" rows={items} columns={requestsCsvColumns} />
        )}
      </div>
      <div className={tableWrapClass}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Дата поступления</th>
              <th className={tableHeadClass}>Клиент</th>
              <th className={tableHeadClass}>Приоритет</th>
              <th className={tableHeadClass}>Статус</th>
              <th className={tableHeadClass}>Позиций</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className={tableCellClass} colSpan={6}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td className={tableCellClass} colSpan={6}>
                  <em>Нет заявок</em>
                </td>
              </tr>
            )}
            {!loading &&
              items.map((row) => {
                const lines = linesByRequest.get(row.id) ?? []
                const open = expandedId === row.id
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={cn(tableRowHoverClass, 'cursor-pointer', open && 'bg-indigo-500/6')}
                      onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                      aria-expanded={open}
                    >
                      <td className={tableCellClass}>
                        <span aria-hidden className="mr-1.5 opacity-65">
                          {open ? '▼' : '▸'}
                        </span>
                        {formatDateRu(row.receivedAt)}
                      </td>
                      <td className={tableCellClass}>{row.clientName}</td>
                      <td className={tableCellClass}>
                        {row.clientUrgent ? (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                            Срочно
                          </span>
                        ) : (
                          <span className="text-sm text-su-text">P{row.priority ?? 3}</span>
                        )}
                      </td>
                      <td className={tableCellClass}>
                        <ProductionStatusBadge kind="request" status={row.status} />
                      </td>
                      <td className={tableCellClass}>{lines.length}</td>
                      <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                        {can(PERMISSION_KEYS.PRODUCTION_REQUESTS_READ) && (
                          <Link
                            to={`/production-requests/${row.id}`}
                            className={btn('ghost', 'sm')}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Открыть
                          </Link>
                        )}
                        {can(PERMISSION_KEYS.PRODUCTION_REQUESTS_DELETE) && (
                          <button
                            type="button"
                            className={cn(btn('danger', 'sm'), 'ml-1')}
                            onClick={(e) => {
                              e.stopPropagation()
                              void removeRow(row.id)
                            }}
                          >
                            Удалить
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-indigo-500/6">
                        <td className={cn(tableCellClass, 'p-3')} colSpan={6}>
                          <div className="rounded-su border border-su-border bg-su-surface-muted/50 p-3">
                            {lines.length === 0 ? (
                              <p className="m-0 text-sm text-su-text">Нет позиций в заявке.</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className={cn(tableClass, 'text-xs')}>
                                  <thead>
                                    <tr>
                                      <th className={tableCompactHeadClass}>#</th>
                                      <th className={tableCompactHeadClass}>Номенклатура</th>
                                      <th className={tableCompactHeadClass}>Статус</th>
                                      <th className={tableCompactHeadClass}>Код</th>
                                      <th className={tableCompactHeadClass}>Кол-во</th>
                                      <th className={tableCompactHeadClass}>План отгрузки</th>
                                      <th className={tableCompactHeadClass}>Производство</th>
                                      <th className={tableCompactHeadClass}>Отгрузка факт</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lines.map((ln, i) => {
                                      const p = ln.expand?.product
                                      const overdue = isLineOverdue({
                                        plannedProduction: ln.plannedProduction,
                                        status: ln.status,
                                      })
                                      return (
                                        <tr key={ln.id} className={tableRowHoverClass}>
                                          <td className={tableCompactCellClass}>{i + 1}</td>
                                          <td className={tableCompactCellClass}>{p?.name ?? '—'}</td>
                                          <td className={tableCompactCellClass}>
                                            <span className="inline-flex flex-wrap items-center gap-1">
                                              <ProductionStatusBadge kind="line" status={ln.status} />
                                              {overdue && (
                                                <span className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[0.65rem] font-semibold text-red-800">
                                                  Просрочено
                                                </span>
                                              )}
                                            </span>
                                          </td>
                                          <td className={tableCompactCellClass}>{p?.code?.trim() ? p.code : '—'}</td>
                                          <td className={tableCompactCellClass}>{formatQty(ln.quantity)}</td>
                                          <td className={tableCompactCellClass}>{formatDateRu(ln.plannedShipManager)}</td>
                                          <td className={tableCompactCellClass}>{formatDateRu(ln.plannedProduction)}</td>
                                          <td className={tableCompactCellClass}>{formatDateRu(ln.actualShip)}</td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <Modal title="Импорт заказа CRM (dev)" onClose={() => setImportOpen(false)}>
          {!crmImportEnabled && (
            <Alert kind="warn">
              Импорт отключён: задайте <code>VITE_CRM_ORDER_IMPORT</code> при сборке фронта.
            </Alert>
          )}
          {importErr && <Alert kind="err">{importErr}</Alert>}
          <p className="text-sm text-su-text">
            Тестовый импорт через <code>importCrmOrder</code>: первый клиент CRM, сопоставление продукции по коду.
          </p>
          <div className="grid gap-3">
            <FormField label="Код продукции" htmlFor="crm-import-code">
              <input
                id="crm-import-code"
                type="text"
                value={importProductCode}
                onChange={(e) => setImportProductCode(e.target.value)}
                disabled={importBusy}
                {...formInputProps()}
              />
            </FormField>
            <FormField label="Количество" htmlFor="crm-import-qty">
              <input
                id="crm-import-qty"
                type="number"
                min={1}
                value={importQty}
                onChange={(e) => setImportQty(e.target.value)}
                disabled={importBusy}
                {...formInputProps()}
              />
            </FormField>
          </div>
          <ModalActions>
            <button type="button" className={modalBtn()} onClick={() => setImportOpen(false)} disabled={importBusy}>
              Отмена
            </button>
            <button
              type="button"
              className={modalBtn('primary')}
              disabled={importBusy || !crmImportEnabled}
              onClick={() => void runCrmImport()}
            >
              {importBusy ? 'Импорт…' : 'Импортировать'}
            </button>
          </ModalActions>
        </Modal>
      )}
    </div>
  )
}
